import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import {pastelColors} from 'lib/Colors';
import {FeatureLibrary, MatchType} from 'molbio/feature-libraries/FeatureLibraryConstants';
import {formatFeatureLibraryName} from 'molbio/feature-libraries/featureLibraryUtil';
import {FONT_FAMILY_MONOSPACED} from 'styles/FontConstants';
import {miniSpacingRight} from 'styles/Utilities';
import {BLUE_DARKER} from 'ui-library/Colors';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import FileChooser from 'ui-library/FileChooser';
import {Header3} from 'ui-library/Headers';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import RadioButtonGroup from 'ui-library/inputs/RadioButtonGroup';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import Modal from 'ui-library/modals/Modal';
import {Cell, Column, Table} from 'ui-library/simple-table/SimpleTable';

const CREATE_NEW = 'CREATE_NEW';
const CREATE_NEW_TEXT = '-- Create new library --';

export interface ImportFeatureLibraryModalProps {
  isCSVImport: boolean;
  libraries: Array<FeatureLibrary>;
  onRequestClose: () => void;
  onRequestSubmit: (result: {file: File; libraryId: number; matchType: MatchType}) => void;
}

function SupportedColorsDescription(): JSX.Element {
  const [isOpen, setOpen] = useState(false);
  return (
    <div>
      <div
        onClick={() => {
          setOpen(!isOpen);
        }}
      >
        <Header3 className={css(styles.colorHeader)}>
          {!isOpen && <BenchlingIcon icon="chevron-right" className={css(styles.headerChevronIcon)} />}
          {isOpen && <BenchlingIcon icon="chevron-down" className={css(styles.headerChevronIcon)} />}
          Supported hex color codes
        </Header3>
      </div>
      {isOpen && (
        <div>
          <p>Any color formatted in Hex Color Code (eg. #FF0000) is mapped to the closest Benchling color.</p>
          <Table isCompact={true} numRows={pastelColors.length}>
            <Column
              header="Standard Colors"
              renderCell={(rowIndex) => (
                <Cell className={css(styles.colorCode)}>
                  <svg height={10} width={15}>
                    <rect fill={pastelColors[rowIndex]} height={10} width={10} />
                  </svg>
                  {pastelColors[rowIndex]}
                </Cell>
              )}
            />
          </Table>
        </div>
      )}
    </div>
  );
}

export default function ImportFeatureLibraryModal({
  isCSVImport,
  libraries,
  onRequestClose,
  onRequestSubmit,
}: ImportFeatureLibraryModalProps): JSX.Element {
  const [file, setFile] = useState<File | null>(null);
  const [selectedLibrary, setSelectedLibrary] = useState<FeatureLibrary | typeof CREATE_NEW>(CREATE_NEW);
  const [matchType, setMatchType] = useState<MatchType>(MatchType.NUCLEOTIDE);
  return (
    <Modal
      buttons={[
        {
          text: 'Upload',
          submit: true,
          disabled: file == null,
        },
        {
          text: 'Cancel',
          cancel: true,
        },
      ]}
      title="Import File"
      onRequestClose={onRequestClose}
      onRequestSubmit={() => {
        onRequestSubmit({
          file: file!,
          libraryId: selectedLibrary === CREATE_NEW ? -1 : selectedLibrary.id,
          matchType,
        });
      }}
    >
      {isCSVImport && (
        <p>
          Import a csv file with format <strong>[name], [feature], [type*], [color*], [match type*]</strong>.
          Type, color, and match type are optional.
        </p>
      )}
      <SupportedColorsDescription />
      <div className="form-group">
        <label>Choose feature library to import into</label>
        <SelectDropdown
          displayName={
            selectedLibrary === CREATE_NEW ? CREATE_NEW_TEXT : formatFeatureLibraryName(selectedLibrary)
          }
          menu={
            <Menu
              height={Menu.HEIGHTS.MEDIUM}
              width={Menu.WIDTHS.FULL}
              value={selectedLibrary}
              scrollable={true}
            >
              {[
                <Option key={CREATE_NEW} value={CREATE_NEW} onSelect={setSelectedLibrary}>
                  {CREATE_NEW_TEXT}
                </Option>,
                ...libraries.map((library) => (
                  <Option
                    key={library.id}
                    value={library.id}
                    onSelect={() => {
                      setSelectedLibrary(library);
                    }}
                    metadata={library.description}
                  >
                    {formatFeatureLibraryName(library)}
                  </Option>
                )),
              ]}
            </Menu>
          }
        />
      </div>
      {isCSVImport && (
        <div className="form-group">
          <RadioButtonGroup<MatchType>
            title="Choose a match type for rows where match type is unspecified:"
            options={[
              {label: 'Nucleotide', value: MatchType.NUCLEOTIDE},
              {label: 'Protein', value: MatchType.PROTEIN},
            ]}
            selectedValue={matchType}
            onChange={setMatchType}
          />
        </div>
      )}
      <p>{isCSVImport ? 'Choose a CSV file below.' : 'Choose an ApE library file (.txt) below.'}</p>
      {file != null && <div className={css(styles.fileName)}>{file.name}</div>}
      <FileChooser
        onSelectFiles={(newFile) => {
          setFile(newFile);
        }}
      />
    </Modal>
  );
}

const styles = StyleSheet.create({
  colorHeader: {
    cursor: 'pointer',
    ':hover': {
      color: BLUE_DARKER,
    },
  },
  colorCode: {
    fontFamily: FONT_FAMILY_MONOSPACED,
  },
  headerChevronIcon: miniSpacingRight,
  fileName: {
    fontFamily: FONT_FAMILY_MONOSPACED,
  },
});
